import { z } from 'zod';

import { cuidLike, nonEmptyString } from '../zod-common';

export const CHAT_PARTICIPANT_ROLES = ['REQUESTER', 'BIDDER', 'GUEST', 'ADMIN_OBSERVER'] as const;
export type ChatParticipantRoleValue = (typeof CHAT_PARTICIPANT_ROLES)[number];

export const MESSAGE_REPORT_REASONS = ['INAPPROPRIATE', 'CIRCUMVENTION', 'SPAM', 'OTHER'] as const;
export type MessageReportReasonValue = (typeof MESSAGE_REPORT_REASONS)[number];

export const MESSAGE_REPORT_ACTIONS = ['NONE', 'HIDDEN', 'SANCTIONED'] as const;
export type MessageReportActionValue = (typeof MESSAGE_REPORT_ACTIONS)[number];

export const MODERATION_CATEGORIES = ['HARASSMENT', 'CIRCUMVENTION', 'SPAM', 'OTHER'] as const;
export type ModerationCategoryValue = (typeof MODERATION_CATEGORIES)[number];

export const MODERATION_STATES = ['NONE', 'PENDING', 'HIDDEN'] as const;
export type ModerationStateValue = (typeof MODERATION_STATES)[number];

export const CHAT_CHANNEL_MAX_MEMBERS = 50;

export const CHAT_MESSAGE_MAX_LENGTH = 4_000;
export const CHAT_CHANNEL_NAME_MAX_LENGTH = 60;
export const CHAT_GUEST_DEFAULT_HOURS = 72;
export const CHAT_GUEST_MAX_HOURS = 24 * 14;

export const chatRoomByProjectSchema = z.object({
  projectId: cuidLike,
});
export type ChatRoomByProjectInput = z.infer<typeof chatRoomByProjectSchema>;

export const chatListMessagesSchema = z.object({
  roomId: cuidLike,
  channelId: cuidLike.nullable().optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
export type ChatListMessagesInput = z.infer<typeof chatListMessagesSchema>;

export const chatSendMessageSchema = z.object({
  roomId: cuidLike,
  channelId: cuidLike.nullable().optional(),
  content: nonEmptyString.max(CHAT_MESSAGE_MAX_LENGTH, 'Max 4000 caractères'),
});
export type ChatSendMessageInput = z.infer<typeof chatSendMessageSchema>;

export const chatListChannelsSchema = z.object({
  roomId: cuidLike,
});
export type ChatListChannelsInput = z.infer<typeof chatListChannelsSchema>;

export const chatCreateChannelSchema = z.object({
  roomId: cuidLike,
  name: nonEmptyString
    .max(CHAT_CHANNEL_NAME_MAX_LENGTH, 'Max 60 caractères')
    .regex(/^[\p{L}\p{N} _\-/]+$/u, 'Lettres, chiffres, espaces, tirets'),
  participantIds: z.array(cuidLike).max(CHAT_CHANNEL_MAX_MEMBERS).optional(),
});
export type ChatCreateChannelInput = z.infer<typeof chatCreateChannelSchema>;

export const chatInviteGuestSchema = z.object({
  roomId: cuidLike,
  email: z.email().trim().toLowerCase(),
  name: nonEmptyString.max(120, 'Max 120 caractères'),
  durationHours: z
    .number()
    .int()
    .min(1)
    .max(CHAT_GUEST_MAX_HOURS)
    .default(CHAT_GUEST_DEFAULT_HOURS),
});
export type ChatInviteGuestInput = z.infer<typeof chatInviteGuestSchema>;

export const CHAT_INVITATION_ID_MAX_LENGTH = 64;
export const CHAT_GUEST_PASSWORD_MAX_LENGTH = 128;

export const chatInvitationIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(CHAT_INVITATION_ID_MAX_LENGTH)
  .regex(/^[A-Za-z0-9_-]+$/, 'Invitation invalide');

export const chatGuestSignInSchema = z.object({
  projectId: cuidLike,
  invitationId: chatInvitationIdSchema,
  email: z.email().trim().toLowerCase(),
  password: nonEmptyString.max(CHAT_GUEST_PASSWORD_MAX_LENGTH),
});
export type ChatGuestSignInInput = z.infer<typeof chatGuestSignInSchema>;

export const CHAT_INVITATION_QUERY_PARAM = 'invitation_id';

export const chatInvitationLookupQuerySchema = z
  .object({ [CHAT_INVITATION_QUERY_PARAM]: chatInvitationIdSchema })
  .transform((query) => ({ invitationId: query[CHAT_INVITATION_QUERY_PARAM] }));
export type ChatInvitationLookupInput = z.output<typeof chatInvitationLookupQuerySchema>;

export const chatOpenDirectChannelSchema = z.object({
  roomId: cuidLike,
  participantId: cuidLike,
});
export type ChatOpenDirectChannelInput = z.infer<typeof chatOpenDirectChannelSchema>;

export const chatGuestIdentitySchema = z.object({
  roomId: cuidLike,
});
export type ChatGuestIdentityInput = z.infer<typeof chatGuestIdentitySchema>;

export const chatActivateGuestSchema = z.object({
  roomId: cuidLike,
  displayName: nonEmptyString.max(80, 'Max 80 caractères'),
});
export type ChatActivateGuestInput = z.infer<typeof chatActivateGuestSchema>;

export const chatReportMessageSchema = z.object({
  messageId: cuidLike,
  reason: z.enum(MESSAGE_REPORT_REASONS),
  comment: z.string().trim().max(1_000).optional(),
});
export type ChatReportMessageInput = z.infer<typeof chatReportMessageSchema>;

export const chatAdminHideMessageSchema = z.object({
  messageId: cuidLike,
  adminAction: z.enum(['HIDDEN', 'SANCTIONED']),
  category: z.enum(MODERATION_CATEGORIES).default('OTHER'),
});
export type ChatAdminHideMessageInput = z.infer<typeof chatAdminHideMessageSchema>;

export const chatAdminUnhideMessageSchema = z.object({
  messageId: cuidLike,
  reason: z.string().trim().max(500).optional(),
});
export type ChatAdminUnhideMessageInput = z.infer<typeof chatAdminUnhideMessageSchema>;
